import React, { useEffect, useState, useRef } from 'react'
import { useWorkStateFields } from '../../store/hooks';
import './CommentBox.css'
import { isMobile, getAuthToken, isAdmin, get_platform_bg_color, get_platform_action_color,
         globalLoader, formatTime } from '../../script/util.js'
import axios from 'axios'
import sendIcon from '../../images/sendIcon.png'
import Popup from '../Popup/Popup'
import lockIcon from '../../images/lock.png'
import pencilIcon from '../../images/pencil.png'
import deleteIcon from '../../images/delete.png'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { COMMENT_TIME_STEP } from '../../script/const.js'

const CommentBox = ({
  isOpen,
  setCommentBox,
  commentBox,
  commentText,
  setCommentText,
  commentStartTime,
  setCommentStartTime,
  commentEndTime,
  setCommentEndTime,
  completedTime,
  playerRef,
}) => {
  const [activeField, setActiveField] = useState('start')
  const [isRangeActive, setIsRangeActive] = useState(false)
  const [comments, setComments] = useState([])
  const [expandedThreads, setExpandedThreads] = useState({})
  const [replyTexts, setReplyTexts] = useState({})
  const [lockPopup, setLockPopup] = useState(false)
  const [editingCommentId, setEditingCommentId] = useState(null)
  const [editingText, setEditingText] = useState('')
  const [deletePopup, setDeletePopup] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState({ threadId: null, commentId: null })
  const [activeTab, setActiveTab] = useState('request')
  const queryParams = new URLSearchParams(window.location.search);
  const collection_id = queryParams.get("work_id")
  const { setShowToast, setToastMessage, setUpdateLoader, updateLoader, catchError } = useWorkStateFields(['updateLoader'])
  const commentInfoPopup = useRef(false);
  const preferThreadIdRef = useRef(null);
  const textAreaRef = useRef(null);

  const getComments = (update=false) => {
    const apiUrl = `${process.env.REACT_APP_STAG_URL}/api/work/comment/get/${collection_id}/`
    const authToken = getAuthToken();
    const headers = {
        Authorization: authToken,
        "Content-Type": "application/json",
    };

    axios({
      method: "GET",
      url: `${apiUrl}`,
      headers
    })
    .then((response) => {
        setComments(response.data)
        if(update){
          const threads = response?.data?.threads || [];
          const latestThread = threads[0];
          if(latestThread){
            const targetThreadId = preferThreadIdRef.current || latestThread.thread_id;
            setExpandedThreads(prev => ({
              ...prev,
              [targetThreadId]: true
            }));
            setTimeout(() => {
              try {
                const threadEl = document.querySelector(`[data-thread-id="${targetThreadId}"]`);
                const lastComment = threadEl?.querySelector('.cb-card-body .cb-comment-row:last-child') || threadEl?.querySelector('.cb-card-header');
                if(lastComment && typeof lastComment.scrollIntoView === 'function'){
                  lastComment.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              } catch(_) {}
            }, 150);
          }
          preferThreadIdRef.current = null;
        }
        (!response.data?.threads || response.data?.threads?.length === 0) && setActiveTab('request')
        (response.data?.is_locked) && setActiveTab('comments')
    })
    .catch((error) => {
        console.error('Error removing image:', error);
    });
  }

  const closeCommentInfoPopup = () => {
    commentInfoPopup.current = false;
    const commentInfoPopupElement = document.querySelector('.commentInfoPopup');
    if(commentInfoPopupElement){
      commentInfoPopupElement.style.display = 'none';
    }
  }

  const getCommentInitials = (comment) => {
    if (!comment) return '?'
    return (
      comment.name_initials ||
      comment.user_initials ||
      (comment.user_name ? comment.user_name.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase() : '?')
    )
  }

  useEffect(() => {
    if(collection_id){
      getComments();
    }
  }, [])
  
  useEffect(() => {
    if (isOpen) setActiveField('start')
  }, [isOpen])

  useEffect(()=>{
     if(playerRef.current?.paused()){
      if(isRangeActive){
        if(activeField === 'start'){
          setCommentStartTime(formatTime(completedTime))
        }else if(activeField === 'end'){
          setCommentEndTime(formatTime(completedTime))
        }
      }else{
        setCommentStartTime(formatTime(completedTime))
        setCommentEndTime(formatTime(completedTime))
      }
     }
  },[completedTime])

  useEffect(()=>{
    const progressBar = document.querySelector('.vjs-progress-holder');
    const commentTooltip = document.querySelector('.commentTooltip');
    if(commentTooltip){
      if(comments?.threads?.length !== 0){
        commentTooltip.style.display = 'none';
        commentInfoPopup.current = false;
      }else{
        commentTooltip.style.display = 'flex';
        commentInfoPopup.current = true;
      }
    }
    if (progressBar) {
      const existingMarkers = progressBar.querySelectorAll('.comment-marker');
      existingMarkers.forEach(marker => marker.remove());

      if (comments?.threads && playerRef.current) {
        const duration = playerRef.current.duration();
        if (duration) {
          const timeGroups = {};
          comments.threads.forEach((thread) => {
            const startTime = thread.start_time;
            if (startTime || startTime === 0) {
              if (!timeGroups[startTime]) timeGroups[startTime] = [];
              timeGroups[startTime].push(thread);
            }
          });

          Object.keys(timeGroups).forEach((startKey) => {
            const startTime = parseFloat(startKey);
            if (!(startTime >= 0)) return;
            const percentage = (startTime / duration) * 100;
            if (percentage < 0 || percentage > 100) return;

            const marker = document.createElement('div');
            marker.className = 'comment-marker';
            marker.style.left = `${percentage}%`;
            marker.style.backgroundColor = 'red';
            marker.style.display = activeTab === 'request' ? 'none' : 'flex';

            const threadsAtTime = timeGroups[startKey];
            if (threadsAtTime.length > 1) {
              marker.textContent = String(threadsAtTime.length);
            } else {
              const firstComment = threadsAtTime[0].comments?.[0];
              const initials = getCommentInitials(firstComment);
              marker.textContent = initials;
            }

            const handleMarkerEvent = (ev) => {
                try { ev.preventDefault() } catch (_) {}
                try { ev.stopPropagation() } catch (_) {}
                try { ev.stopImmediatePropagation && ev.stopImmediatePropagation() } catch (_) {}
              setCommentBox(true)
              try { playerRef.current?.currentTime(startTime) } catch (_) {}
              // Expand the clicked thread(s) and scroll to the first one
              try {
                setExpandedThreads(prev => {
                  const next = { ...prev }
                  threadsAtTime.forEach(t => { if (t?.thread_id != null) next[t.thread_id] = true })
                  return next
                })
                const firstId = threadsAtTime?.[0]?.thread_id
                if (firstId != null) {
                  setTimeout(() => {
                    const el = document.querySelector(`[data-thread-id="${firstId}"]`)
                    if (el && typeof el.scrollIntoView === 'function') {
                      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                    }
                  }, 100)
                }
              } catch (_) {}
              }

            marker.addEventListener('mousedown', handleMarkerEvent, true)
            marker.addEventListener('mouseup', handleMarkerEvent, true)
            marker.addEventListener('touchstart', handleMarkerEvent, { passive: false })
            marker.addEventListener('click', handleMarkerEvent, true)

            progressBar.appendChild(marker);
          });
        }
      }
    }
  },[comments, playerRef.current?.duration()])

  useEffect(()=>{
    const progressBar = document.querySelector('.vjs-progress-holder');
    if(progressBar){
      if(activeTab === 'request'){
        progressBar.querySelectorAll('.comment-marker').forEach(marker => marker.style.display = 'none');
      }else{
        progressBar.querySelectorAll('.comment-marker').forEach(marker => marker.style.display = 'flex');
      }
    }
  },[activeTab, comments])

  useEffect(()=>{
    if(!commentBox){
      comments?.is_locked ? setActiveTab('comments') : setActiveTab('request')
    }
  },[commentBox, comments])
  if (!isOpen) return null
  const min = 0
  const max = playerRef.current?.duration() || 100

  // Parse formatted time input (M:SS or MM:SS) to seconds
  function parseFormattedTime(str) {
    if (typeof str !== 'string') return NaN;
    const parts = str.split(':');
    if (parts.length === 1) {
      // Only seconds
      return parseFloat(parts[0]);
    }
    if (parts.length === 2) {
      const m = parseInt(parts[0], 10);
      const s = parseFloat(parts[1]);
      if (isNaN(m) || isNaN(s)) return NaN;
      return m * 60 + s;
    }
    if (parts.length === 3) {
      const h = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      const s = parseFloat(parts[2]);
      if (isNaN(h) || isNaN(m) || isNaN(s)) return NaN;
      return h * 3600 + m * 60 + s;
    }
    return NaN;
  }

  let start = commentStartTime !== null ? parseFormattedTime(commentStartTime) : 0
  let end = commentEndTime !== null ? parseFormattedTime(commentEndTime) : max
  if (start < min) start = min
  if (end > max) end = max
  if (end < start) end = start

  const animateProgressTo = (targetSeconds) => {
    try {
      if (!playerRef.current) return;
      const progressBar = document.querySelector('.vjs-progress-holder');
      progressBar.style.fontSize = '2em';
      if (!progressBar) return;
      const duration = playerRef.current.duration && playerRef.current.duration();
      if (!duration || !isFinite(duration)) return;

      const pct = Math.max(0, Math.min(100, (targetSeconds / duration) * 100));

      // Remove any existing focus elements
      const existingBar = progressBar.querySelector('.cb-focus-anim');
      if (existingBar && existingBar.parentNode) existingBar.parentNode.removeChild(existingBar);
      const existingPulse = progressBar.querySelector('.cb-focus-pulse');
      if (existingPulse && existingPulse.parentNode) existingPulse.parentNode.removeChild(existingPulse);

      // Animated bar fill
      const bar = document.createElement('div');
      bar.className = 'cb-focus-anim';
      bar.style.backgroundColor = 'red'
      bar.style.width = '0%';
      progressBar.appendChild(bar);

      // Pulse marker
      const pulse = document.createElement('div');
      pulse.className = 'cb-focus-pulse';
      pulse.style.left = pct + '%';
      pulse.style.borderColor = 'red';
      pulse.style.backgroundColor = 'red';
      progressBar.appendChild(pulse);

      // Kick off animations
      requestAnimationFrame(() => {
        bar.style.width = pct + '%';
        pulse.classList.add('cb-focus-pulse-active');
      });

      // Cleanup after animations
      const timeout = setTimeout(() => {
        try { bar && bar.parentNode && bar.parentNode.removeChild(bar); } catch (_) {}
        try { pulse && pulse.parentNode && pulse.parentNode.removeChild(pulse); } catch (_) {}
        progressBar.style.fontSize = '1em';
      }, 900);

      return () => {
        clearTimeout(timeout);
      }
    } catch (_) {}
  }

  // Nudge the selected timestamp one second at a time via the stepper buttons
  const maxStep = Math.floor(max)

  const getStepBounds = (field) => {
    if (field === 'end') return { lower: Math.floor(start), upper: maxStep }
    return { lower: min, upper: isRangeActive ? Math.floor(end) : maxStep }
  }

  const stepTime = (field, delta) => {
    const current = field === 'end' ? end : start
    if (isNaN(current)) return
    const { lower, upper } = getStepBounds(field)
    const next = Math.max(lower, Math.min(Math.floor(current) + delta, upper))
    if (next === current) return
    const formatted = formatTime(next)
    if (field === 'end') {
      setCommentEndTime(formatted)
    } else {
      setCommentStartTime(formatted)
      if (!isRangeActive) setCommentEndTime(formatted)
    }
    setActiveField(field)
    try { playerRef.current?.currentTime(next) } catch (_) {}
    animateProgressTo(next)
  }

  const renderTimeField = (field, inputProps = {}) => {
    const label = field === 'end' ? 'End time' : isRangeActive ? 'Start time' : 'Time'
    const value = field === 'end' ? commentEndTime : commentStartTime
    const seconds = field === 'end' ? end : start
    const hasValue = value !== null && value !== undefined && value !== '' && !isNaN(seconds)
    const { lower, upper } = getStepBounds(field)
    return (
      <div className='cb-time-stepper'>
        <button
          type='button'
          className='cb-step-btn'
          aria-label={`${label} minus 1 second`}
          disabled={!hasValue || Math.floor(seconds) <= lower}
          onMouseDown={e => e.preventDefault()}
          onClick={() => stepTime(field, -COMMENT_TIME_STEP)}
        >
          &minus;
        </button>
        <input
          type='text'
          inputMode='none'
          value={hasValue ? formatTime(seconds) : ''}
          readOnly
          onFocus={() => {
            setActiveField(field)
            animateProgressTo(seconds)
          }}
          className='cb-timebox'
          placeholder="0:00"
          {...inputProps}
        />
        <button
          type='button'
          className='cb-step-btn'
          aria-label={`${label} plus 1 second`}
          disabled={!hasValue || Math.floor(seconds) >= upper}
          onMouseDown={e => e.preventDefault()}
          onClick={() => stepTime(field, COMMENT_TIME_STEP)}
        >
          +
        </button>
      </div>
    )
  }

  const submitComment = (thread_id_param=null) => {
    if(isAdmin()){
        return;
    }
    setUpdateLoader(true)
    const thread_id = (typeof thread_id_param === 'number' || typeof thread_id_param === 'string') ? thread_id_param : null
    var apiUrl = `${process.env.REACT_APP_STAG_URL}/api/work/comment/add/${collection_id}/`

    const authToken = getAuthToken();
    const headers = {
        Authorization: authToken,
        "Content-Type": "application/json",
    };
    let data = { start_time: parseFormattedTime(commentStartTime), end_time: parseFormattedTime(commentEndTime), comment: commentText };
    if(thread_id){
      apiUrl = `${apiUrl}${thread_id}/`
      const text = replyTexts[thread_id]?.trim()
      if(text){
        data.comment = text;
      }
    }
    axios({
      method: "POST",
      url: `${apiUrl}`,
      data: data,
      headers
    })
    .then(() => {
        setCommentText('');
        preferThreadIdRef.current = thread_id || null;
        getComments(true);
        setReplyTexts(prev => ({ ...prev, [thread_id]: '' }))
    })
    .catch((error) => {
        console.error('Error removing image:', error);
    })
    .finally(() => {
        setUpdateLoader(false)
    });
  }

  const updateComment = ( commentId, newText) => {
    if(isAdmin()){
      return;
    }
    setUpdateLoader(true)
    const apiUrl = `${process.env.REACT_APP_STAG_URL}/api/work/comment/edit/${collection_id}/${commentId}/`
    const authToken = getAuthToken();
    const headers = {
      Authorization: authToken,
      'Content-Type': 'application/json',
    };
    return axios({ method: 'PUT', url: `${apiUrl}`, data: { comment: newText }, headers })
    .catch((error) => {
        setShowToast(true)
        setToastMessage(error.response.data.toast_data)
    })
    .finally(() => {
        setUpdateLoader(false)
    })
  }

  const removeComment = (threadId, commentId) => {
    if(isAdmin()){
      return;
    }
    setUpdateLoader(true)
    const apiUrl = `${process.env.REACT_APP_STAG_URL}/api/work/comment/delete/${collection_id}/${threadId}/${commentId}/`
    const authToken = getAuthToken();
    const headers = {
      Authorization: authToken,
      'Content-Type': 'application/json',
    };
    return axios({ method: 'DELETE', url: `${apiUrl}`, headers })
    .catch((error) => {
        setShowToast(true)
        setToastMessage(error.response.data.toast_data)
    })
    .finally(() => {
        setUpdateLoader(false)
    })
  }

  const hasDuration = playerRef.current?.duration() !== undefined
  const isInvalidRange =
    commentStartTime === null ||
    commentEndTime === null ||
    commentStartTime < 0 ||
    commentEndTime < commentStartTime ||
    (hasDuration && commentEndTime > playerRef.current.duration())

  const lockConversation = () => {
    if(isAdmin()){
      return;
    }
    setUpdateLoader(true)
    const authToken = getAuthToken();
    const headers = {
        Authorization: authToken,
        "Content-Type": "application/json",
    };
    const api_url = `${process.env.REACT_APP_STAG_URL}/api/work/comment/lock/${collection_id}/`
    axios
    .put(`${api_url}`, {} , { headers })
    .then((response)=>{
        setShowToast(true)
        setToastMessage(response.data.toast_data)
        setActiveTab('comments')
        getComments()
    })
    .catch((error)=>{
        setShowToast(true)
        setToastMessage(error.response.data.toast_data)
    })
    .finally(() => {
        setUpdateLoader(false)
    })
  }

  const openCommentInfoPopup = () => {
    commentInfoPopup.current = true;
    const commentInfoPopupElement = document.querySelector('.commentInfoPopup');
    if(commentInfoPopupElement){
      commentInfoPopupElement.style.display = 'flex';
    }
  }

  const handleClose = () => {
    setIsRangeActive(false)
    setExpandedThreads({})
    if (setCommentBox) setCommentBox(false)
  }

  const scrollInputAboveKeyboard = (el) => {
    try {
      if (!el) return;
      if (!isMobile()) return;
      // Give the keyboard a moment to appear, then scroll the focused input into view
      setTimeout(() => {
        try {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (_) {}
        // Extra nudge for iOS Safari
        try {
          const panel = document.querySelector('.cb-panel');
          if (panel) {
            panel.scrollTop = el.offsetTop - 100;
          }
        } catch (_) {}
      }, 120);
    } catch (_) {}
  }

  const isCommentAuthor = (comment) => {
    if (!comment?.user_id) return false;
    
    try {
      const authDictStr = localStorage.getItem('auth_dict');
      if (authDictStr) {
        const authDict = JSON.parse(authDictStr);
        const currentUserId = authDict.eventGraphiaId;
        
        // If the logged-in user ID matches the comment's user_id, they are the creator
        return String(comment.user_id) === String(currentUserId);
      }
    } catch (e) {
      console.warn("Could not parse auth_dict for user verification");
    }

    return false;
  };

  return (
    <div className={`cb-overlay ${isMobile() ? 'cb-overlay-mobile' : ''}`} onClick={handleClose}>
      {updateLoader && <div className="overlayLoader">{globalLoader()}</div>}
      <div className={`cb-panel ${isMobile() ? 'cb-panel-mobile' : ''}`} style={{ paddingBottom: isMobile() ? 0.3*window.innerHeight + 'px' : '2rem' }} onClick={e => e.stopPropagation()}>
        <div className='cb-header'>
          <div className='cb-tabs' role='tablist' aria-label='Comments Tabs'>
            {!comments?.is_locked && <button
              role='tab'
              aria-selected={activeTab === 'request'}
              className={`cb-tab ${activeTab === 'request' ? 'cb-tab-active' : ''}`}
              onClick={() => { setActiveTab('request'); setExpandedThreads({}) }}
              type='button'
            >
              Request a Change <FontAwesomeIcon icon={faCircleInfo} style={{color: "#ffffff", cursor:"pointer", marginLeft: '6px'}} onClick={openCommentInfoPopup} />
            </button>}
            {(comments?.threads && comments?.threads?.length > 0) && <button
              role='tab'
              aria-selected={activeTab === 'comments'}
              className={`cb-tab ${activeTab === 'comments' ? 'cb-tab-active' : ''}`}
              onClick={() => {
                setActiveTab('comments')
                try {
                  const threads = comments?.threads || []
                  const latestThread = threads[0]
                  setExpandedThreads({})
                  if (latestThread && latestThread.thread_id != null) {
                    setExpandedThreads(prev => ({ ...prev, [latestThread.thread_id]: true }))
                    try { playerRef.current?.currentTime(latestThread.start_time) } catch (_) {}
                    setTimeout(() => {
                      try {
                        const el = document.querySelector(`[data-thread-id="${latestThread.thread_id}"]`)
                        if (el && typeof el.scrollIntoView === 'function') {
                          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                        }
                      } catch (_) {}
                    }, 120)
                  }
                } catch (_) {}
              }}
              type='button'
            >
              All comments
            </button>}
          </div>
          <button className='cb-close' onClick={handleClose} aria-label='Close'>
            &times;
          </button>
        </div>
        {activeTab === 'request' && (
        <div>
          <div className="cb-toggle-row" style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem', justifyContent: 'space-between' }}>
            <button
              className="cb-lock-btn"
              disabled={comments?.is_locked}
              style={{
                background: `${get_platform_action_color()}`,
                marginRight: '1rem',
                opacity: comments?.threads?.length === 0 || comments?.is_locked ? 0.5 : 1,
              }}
              onClick={() => {
                if(comments?.is_locked){
                  return;
                }
                if(!comments?.threads || comments?.threads?.length === 0){
                  setShowToast(true)
                  setToastMessage({'title': 'No comments to lock'})
                  return;
                }
                setLockPopup(true)
              }}
              type="button"
            >
              {comments?.is_locked ? 'Locked' : 'Lock'}
            </button>
            <div style={{ display: comments?.is_locked ? 'none' : 'block' }}>
              <label htmlFor="cb-enable-range" style={{ marginRight: '0.5rem', fontWeight: 500, fontSize: '0.8rem' }}>
                Time Range
              </label>
              <div
                id="cb-enable-range"
                role="button"
                tabIndex={0}
                aria-pressed={commentEndTime !== null && commentEndTime !== undefined && commentEndTime !== '' && parseFormattedTime(commentEndTime) !== parseFormattedTime(commentStartTime)}
                onClick={() => {
                  const isRange =
                    commentEndTime !== null &&
                    commentEndTime !== undefined &&
                    commentEndTime !== '' &&
                    parseFormattedTime(commentEndTime) !== parseFormattedTime(commentStartTime);
                  if (isRange) {
                    setCommentEndTime(formatTime(parseFormattedTime(commentStartTime)));
                    setIsRangeActive(false)
                  } else {
                    let newEnd = Math.min(parseFormattedTime(commentStartTime) + 1, max);
                    setCommentEndTime(formatTime(newEnd));
                    setIsRangeActive(true)
                  }
                }}
                onKeyDown={e => {
                  if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    const isRange =
                      commentEndTime !== null &&
                      commentEndTime !== undefined &&
                      commentEndTime !== '' &&
                      parseFormattedTime(commentEndTime) !== parseFormattedTime(commentStartTime);
                    if (isRange) {
                      setCommentEndTime(formatTime(parseFormattedTime(commentStartTime)));
                    } else {
                      let newEnd = Math.min(parseFormattedTime(commentStartTime) + 1, max);
                      setCommentEndTime(formatTime(newEnd));
                    }
                  }
                }}
                style={{
                  width: '1.5rem',
                  height: '0.8rem',
                  borderRadius: '12px',
                  background: (commentEndTime !== null && commentEndTime !== undefined && commentEndTime !== '' && parseFormattedTime(commentEndTime) !== parseFormattedTime(commentStartTime)) ? '#4caf50' : '#ccc',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                  outline: 'none',
                  display: 'inline-block',
                  marginRight: '0.5rem',
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    left: (commentEndTime !== null && commentEndTime !== undefined && commentEndTime !== '' && parseFormattedTime(commentEndTime) !== parseFormattedTime(commentStartTime)) ? '0.8rem' : '0.1rem',
                    top: '0',
                    width: '0.8rem',
                    height: '0.8rem',
                    borderRadius: '50%',
                    background: '#fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                    transition: 'left 0.2s',
                    display: 'block',
                  }}
                />
              </div>
            </div>
          </div>
          
          {/* If range is disabled, show only a single time input, else show range */}
          {!(isRangeActive) && (
            <div className='cb-field' style={{ marginBottom: '1.5rem', display: comments?.is_locked ? 'none' : 'block' }}>
              <label className='cb-label' style={{ marginBottom: '0.5rem' }}>Select Time</label>
              {renderTimeField('start', { autoFocus: true })}
            </div>
          )}
          {isRangeActive && <div className='cb-field' style={{ display: comments?.is_locked ? 'none' : 'block' }}>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-around' }}>
              <span style={{ fontSize: '0.9rem', color: '#aaa', marginBottom: '0.25rem', marginRight: '1.5rem' }}>Start</span>
              <label className='cb-label'>Select Time Range</label>
              <span style={{ fontSize: '0.9rem', color: '#aaa', marginBottom: '0.25rem', marginLeft: '1.5rem' }}>End</span>
            </div>
            <div className='cb-range-row' style={{ alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {renderTimeField('start')}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {renderTimeField('end', { autoFocus: true })}
              </div>
            </div>
          </div>}

          <textarea
            className='cb-textarea'
            placeholder='Write your comment here...'
            value={commentText || ''}
            onChange={e => setCommentText(e.target.value)}
            onFocus={(e) => scrollInputAboveKeyboard(e.target)}
            ref={textAreaRef}
            style={{ display: comments?.is_locked ? 'none' : 'block' }}
          />

          <div className='cb-actions' style={{ display: comments?.is_locked ? 'none' : 'flex' }}>
            <button className='cb-btn cb-btn-neutral' onClick={handleClose}>Cancel</button>
            <button
              className='cb-btn cb-btn-primary'
              onClick={() => submitComment()}
              disabled={!commentText || commentText.trim() === '' || isInvalidRange}
            >
              Comment
            </button>
          </div>
          {(
          <div className='cb-comments'>
            {comments?.threads
              ?.filter(thread => thread.start_time == start && thread.end_time == end)
              ?.map((thread) => {
                const isOpen = true
                const toggle = () => {
                  setExpandedThreads(prev => ({ ...prev, [thread.thread_id]: true }))
                  playerRef.current?.currentTime(thread.start_time)
                }
                return (
                  <div className='cb-card' key={thread.thread_id} data-thread-id={thread.thread_id}>
                    <button className='cb-card-header' onClick={toggle} aria-expanded={isOpen}>
                      <div className='cb-card-title'>
                        <span className='cb-badge'>{thread.comments?.length || 0}</span>
                        <span className='cb-card-text'>Comments</span>
                      </div>
                      <div className='cb-card-meta'>
                        <span className='cb-time'>{formatTime(thread.start_time)}{start !== end && `-${formatTime(thread.end_time)}`}</span>
                        <span className={`cb-chevron ${isOpen ? 'cb-chevron-open' : ''}`} aria-hidden>
                          ▾
                        </span>
                      </div>
                    </button>
                    {isOpen && (
                      <div className='cb-card-body'>
                        {thread.comments?.map((comment) => {
                          const isSelf = isCommentAuthor(comment)
                          const displayName = comment.user_name || comment.name_initials || comment.user_initials
                          const displayNameTruncated = (displayName && displayName.length > 20) ? (displayName.slice(0, 20) + '...') : displayName
                          return (
                            <div
                              className={`cb-comment-row cb-comment-row-flex ${isSelf ? 'cb-comment-row-self' : 'cb-comment-row-other'}`}
                              key={comment.id}
                            >
                              <div className='cb-comment-avatar' style={{ backgroundColor: get_platform_bg_color() }} aria-hidden>
                                {getCommentInitials(comment)}
                              </div>
                              <div className={`cb-comment-content cb-comment-bubble ${isSelf ? 'cb-comment-bubble-self' : ''}`}>
                                  <div className={`cb-comment-head ${isSelf ? 'cb-comment-text-right' : ''}`}>
                                  {isSelf && !comments?.is_locked && (
                                    <div className='cb-actions-inline' aria-hidden>
                                        <img
                                          src={pencilIcon}
                                          alt='Edit'
                                          className='cb-action-icon invert-image'
                                          onClick={() => {
                                            setEditingCommentId(comment.id)
                                            setEditingText(comment.text || '')
                                          }}
                                        />
                                      <img
                                        src={deleteIcon}
                                        alt='Delete'
                                        className='cb-action-icon-delete invert-image'
                                        onClick={() => {
                                          setDeleteTarget({ threadId: thread.thread_id, commentId: comment.id })
                                          setDeletePopup(true)
                                        }}
                                      />
                                    </div>
                                  )}
                                  <div className={'cb-comment-username'}>~{displayNameTruncated}</div>
                                </div>
                                  {editingCommentId === comment.id ? (
                                    <div className='cb-edit-inline'>
                                      <input
                                        type='text'
                                        className='cb-edit-input'
                                        value={editingText}
                                        onKeyDown={e => {
                                          if (e.key === 'Enter') {
                                            try { e.preventDefault(); } catch(_) {}
                                            const text = (editingText || '').trim();
                                            if (text) {
                                              updateComment(comment.id, text)
                                                .then(() => {
                                                  setEditingCommentId(null)
                                                  setEditingText('')
                                                  getComments()
                                                })
                                                .catch((error) => catchError && catchError(error))
                                            }
                                          }
                                        }}
                                        onChange={e => setEditingText(e.target.value)}
                                        autoFocus
                                      />
                                      <div className='cb-edit-actions'>
                                        <button
                                          className='cb-btn'
                                          onClick={() => {
                                            const text = (editingText || '').trim()
                                            if (!text) return;
                                            updateComment(comment.id, text)
                                              .then(() => {
                                                setEditingCommentId(null)
                                                setEditingText('')
                                                getComments()
                                              })
                                              .catch((error) => catchError && catchError(error))
                                          }}
                                          aria-label="Save"
                                          style={{ padding: '5px 7px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#44aa44', color: '#fff' }}
                                        >
                                          <span role="img" aria-label="Check" style={{ fontSize: '0.65em' }}>✔️</span>
                                        </button>
                                        <button
                                          className='cb-btn cb-btn-neutral'
                                          onClick={() => {
                                            setEditingCommentId(null)
                                            setEditingText('')
                                          }}
                                          aria-label="Cancel"
                                          style={{ padding: '5px 7px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        >
                                          <span role="img" aria-label="Cross" style={{ fontSize: '0.65em' }}>❌</span>
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className={`cb-comment-text cb-comment-text-wrap ${isSelf ? 'cb-comment-text-right' : ''}`}>{comment.text}</div>
                                  )}
                              </div>
                            </div>
                          )
                        })}
                        {!comments?.is_locked && <div className='cb-reply-row'>
                          <input
                            type='text'
                            value={replyTexts[thread.thread_id] || ''}
                            onChange={e => setReplyTexts(prev => ({ ...prev, [thread.thread_id]: e.target.value }))}
                            onFocus={(e) => scrollInputAboveKeyboard(e.target)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') {
                                try { e.preventDefault(); } catch(_) {}
                                const text = (replyTexts[thread.thread_id] || '').trim();
                                if (text) submitComment(thread.thread_id);
                              }
                            }}
                            placeholder='Write a reply'
                            className='cb-reply-input'
                          />
                          <button
                            className='invert-image'
                            onClick={() => submitComment(thread.thread_id)}
                            disabled={!replyTexts[thread.thread_id] || replyTexts[thread.thread_id].trim() === ''}
                            aria-label='Reply'
                          >
                            <img src={sendIcon} alt='Send' style={{ width: '16px', height: '16px' }} />
                          </button>
                        </div>}
                      </div>
                    )}
                  </div>
                )
              })}
          </div>
          )}
        </div>
        )}
        {activeTab === 'comments' && (
        <div className='cb-comments'>
          {(!comments?.threads || comments?.threads?.length === 0) && (
            <div style={{ color: '#a7a7a7', fontSize: '0.9rem', padding: '0.4rem 0.1rem' }}>No comments!</div>
          )}
          {comments?.threads?.map((thread) => {
            const isOpen = !!expandedThreads[thread.thread_id]
            const toggle = () => {
              setExpandedThreads(prev => ({ ...prev, [thread.thread_id]: !prev[thread.thread_id] }))
              playerRef.current?.currentTime(thread.start_time)
            }
            return (
              <div className='cb-card' key={thread.thread_id} data-thread-id={thread.thread_id}>
                <button className='cb-card-header' onClick={toggle} aria-expanded={isOpen}>
                  <div className='cb-card-title'>
                    <span className='cb-badge'>{thread.comments?.length || 0}</span>
                    <span className='cb-card-text'>Comments</span>
                  </div>
                  <div className='cb-card-meta'>
                    <span className='cb-time'>{formatTime(thread.start_time)}</span>
                    {thread.start_time !== thread.end_time && <span className='cb-time-sep'>—</span>}
                    {thread.start_time !== thread.end_time && <span className='cb-time'>{formatTime(thread.end_time)}</span>}
                    <span className={`cb-chevron ${isOpen ? 'cb-chevron-open' : ''}`} aria-hidden>
                      ▾
                    </span>
                  </div>
                </button>
                {isOpen && (
                  <div className='cb-card-body'>
                    {thread.comments?.map((comment) => {
                      const isSelf = isCommentAuthor(comment)
                      const displayName = comment.user_name || comment.name_initials || comment.user_initials
                      const displayNameTruncated = (displayName && displayName.length > 20) ? (displayName.slice(0, 20) + '...') : displayName
                      return (
                        <div
                          className={`cb-comment-row cb-comment-row-flex ${isSelf ? 'cb-comment-row-self' : 'cb-comment-row-other'}`}
                          key={comment.id}
                        >
                          <div className='cb-comment-avatar' style={{ backgroundColor: get_platform_bg_color() }} aria-hidden>
                            {getCommentInitials(comment)}
                          </div>
                          <div className={`cb-comment-content cb-comment-bubble ${isSelf ? 'cb-comment-bubble-self' : ''}`}>
                              <div className={`cb-comment-head ${isSelf ? 'cb-comment-text-right' : ''}`}>
                              {isSelf && !comments?.is_locked && (
                                <div className='cb-actions-inline' aria-hidden>
                                    <img
                                      src={pencilIcon}
                                      alt='Edit'
                                      className='cb-action-icon invert-image'
                                      onClick={() => {
                                        setEditingCommentId(comment.id)
                                        setEditingText(comment.text || '')
                                      }}
                                    />
                                  <img
                                    src={deleteIcon}
                                    alt='Delete'
                                    className='cb-action-icon-delete invert-image'
                                    onClick={() => {
                                      setDeleteTarget({ threadId: thread.thread_id, commentId: comment.id })
                                      setDeletePopup(true)
                                    }}
                                  />
                                </div>
                              )}
                              <div className={'cb-comment-username'}>~{displayNameTruncated}</div>
                            </div>
                              {editingCommentId === comment.id ? (
                                <div className='cb-edit-inline'>
                                  <input
                                    type='text'
                                    className='cb-edit-input'
                                    value={editingText}
                                    onChange={e => setEditingText(e.target.value)}
                                    autoFocus
                                  />
                                  <div className='cb-edit-actions'>
                                    <button
                                      className='cb-btn'
                                      style={{ padding: '5px 7px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#44aa44', color: '#fff' }}
                                      onClick={() => {
                                        const text = (editingText || '').trim()
                                        if (!text) return;
                                        updateComment(comment.id, text)
                                          .then(() => {
                                            setEditingCommentId(null)
                                            setEditingText('')
                                            getComments()
                                          })
                                          .catch((error) => catchError && catchError(error))
                                      }}
                                      aria-label="Save"
                                    >
                                      <span role="img" aria-label="Check" style={{ fontSize: '0.65em' }}>✔️</span>
                                    </button>
                                    <button
                                      className='cb-btn cb-btn-neutral'
                                      onClick={() => {
                                        setEditingCommentId(null)
                                        setEditingText('')
                                      }}
                                      aria-label="Cancel"
                                      style={{ padding: '5px 7px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    >
                                      <span role="img" aria-label="Cross" style={{ fontSize: '0.65em' }}>❌</span>
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className={`cb-comment-text cb-comment-text-wrap ${isSelf ? 'cb-comment-text-right' : ''}`}>{comment.text}</div>
                              )}
                          </div>
                        </div>
                      )
                    })}
                    {!comments?.is_locked && <div className='cb-reply-row'>
                      <input
                        type='text'
                        value={replyTexts[thread.thread_id] || ''}
                        onChange={e => setReplyTexts(prev => ({ ...prev, [thread.thread_id]: e.target.value }))}
                        onFocus={(e) => scrollInputAboveKeyboard(e.target)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            try { e.preventDefault(); } catch(_) {}
                            const text = (replyTexts[thread.thread_id] || '').trim();
                            if (text) submitComment(thread.thread_id);
                          }
                        }}
                        placeholder='Write a reply'
                        className='cb-reply-input'
                      />
                      <button
                        className='invert-image'
                        onClick={() => submitComment(thread.thread_id)}
                        disabled={!replyTexts[thread.thread_id] || replyTexts[thread.thread_id].trim() === ''}
                        aria-label='Reply'
                      >
                        <img src={sendIcon} alt='Send' style={{ width: '16px', height: '16px' }} />
                      </button>
                    </div>}
                  </div>
                )}
              </div>
            )
          })}
        </div>
        )}
      </div>
      {lockPopup && (
        <div onClick={e => e.stopPropagation()}>
      <Popup isOpen={lockPopup} >
        <div style={{textAlign:"left", padding:"10px", maxWidth: "500px"}}>
            <h3>Lock your requested changes</h3>
            <div style={{padding:"10px 0", fontWeight: "bold", color: "red"}}>Warning: Once locked, changes cannot be edited!</div>
            <div style={{display:"flex", justifyContent:"flex-end", fontSize:"medium"}}>
                <button style={{border:"1px solid black", padding:"3px 5px", borderRadius:"10px"}} onClick={()=>setLockPopup(false)}>Cancel</button>
                <button style={{border:`1px solid ${get_platform_action_color()}`, color:"white", background: get_platform_action_color(), padding:"3px 5px", borderRadius:"10px", display:"flex", margin: "0 10px", alignItems:"center"}} 
                onClick={()=>{setLockPopup(false); lockConversation()}}>
                    <img className="invert-image" style={{width: '18px', marginRight:"4px", minWidth:"18px", height:"18px"}} src={lockIcon}/>{" "}Lock
                </button>
            </div>
        </div>
      </Popup>
        </div>
      )}
      {deletePopup && (
        <div onClick={e => e.stopPropagation()}>
      <Popup isOpen={deletePopup} >
        <div style={{textAlign:"left", padding:"10px", maxWidth: "500px"}}>
            <h3>Delete Comment</h3>
            <div style={{padding:"10px 0"}}>This will permanently delete the comment. This action cannot be undone.</div>
            <div style={{display:"flex", justifyContent:"flex-end", fontSize:"medium"}}>
                <button style={{border:"1px solid black", padding:"3px 5px", borderRadius:"10px"}} onClick={()=>setDeletePopup(false)}>Cancel</button>
                <button
                  style={{border:`1px solid ${get_platform_action_color()}`, color:"white", background: get_platform_action_color(), padding:"3px 5px", borderRadius:"10px", display:"flex", margin: "0 10px", alignItems:"center"}}
                  onClick={()=>{
                    setDeletePopup(false);
                    removeComment(deleteTarget.threadId, deleteTarget.commentId)
                      .then(()=>{
                        setDeleteTarget({ threadId: null, commentId: null })
                        getComments()
                      })
                      .catch((error)=>{
                        setDeleteTarget({ threadId: null, commentId: null })
                        catchError && catchError(error)
                      })
                  }}
                >
                  Delete
                </button>
            </div>
        </div>
      </Popup>
        </div>
      )}
      <div className='commentInfoPopup' style={{ display: commentInfoPopup.current ? 'block' : 'none' }}>
        <Popup isOpen={true}>
          <div style={{textAlign:"left", padding:"10px", maxWidth: "500px"}}>
            <h3>Request Changes</h3>
            <div style={{padding:"10px 0"}}>
              <ul>
                <li style={{fontSize: "inherit"}}>Comment on a specific time or time range in the video to request changes.</li>
                <li style={{fontSize: "inherit"}}>Once you lock it, the changes will be finalized.</li>
                <li style={{fontSize: "inherit"}}>Our team will then review the submitted changes.</li>
              </ul>
            </div>
            <div style={{display:"flex", justifyContent:"flex-end", fontSize:"medium"}}>
              <button style={{border:"1px solid black", padding:"3px 5px", borderRadius:"10px"}} onClick={(e)=>{e.stopPropagation(); closeCommentInfoPopup()}}>Okay</button>
            </div>
          </div>
        </Popup>
      </div>
    </div>
  );
}

export default CommentBox;
